/**
 * Adaptive GAPI proxy resolution — hosts and paths for Auth / Firestore /
 * Storage / App Check when Google APIs are blocked but alea-tui Hosting works.
 */

import { isHostingWebOrigin } from './functionsHost';

export type GapiTransportMode = 'direct' | 'proxy';
export type GapiProxyModeSetting = 'auto' | 'force' | 'off';

export const GAPI_UPSTREAM = {
  identityToolkit: 'identitytoolkit.googleapis.com',
  secureToken: 'securetoken.googleapis.com',
  firestore: 'firestore.googleapis.com',
  firebaseStorage: 'firebasestorage.googleapis.com',
  cloudStorage: 'storage.googleapis.com',
  appCheck: 'content-firebaseappcheck.googleapis.com',
} as const;

/** Prefer custom domain, then Firebase Hosting (Cuba often reaches one but not both). */
export const FALLBACK_HOSTING_ORIGINS = [
  'https://alea-tui.com',
  'https://alea-tui-platform.web.app',
] as const;

const DEFAULT_PROXY_ORIGIN = FALLBACK_HOSTING_ORIGINS[0];

export function readGapiProxyModeSetting(
  raw: string | undefined = import.meta.env.VITE_GAPI_PROXY_MODE,
): GapiProxyModeSetting {
  const value = String(raw ?? 'auto').trim().toLowerCase();
  if (value === 'force' || value === 'off' || value === 'auto') return value;
  return 'auto';
}

function stripTrailingSlash(origin: string): string {
  return origin.replace(/\/$/, '');
}

function pushUniqueHostingOrigin(out: string[], value: string | null | undefined): void {
  const trimmed = String(value ?? '').trim();
  if (!trimmed || !isHostingWebOrigin(trimmed)) return;
  const normalized = stripTrailingSlash(trimmed);
  if (!out.includes(normalized)) out.push(normalized);
}

/**
 * Ordered Hosting origins to try for `/gapi` + Capacitor callables.
 * Env → page origin (if Hosting) → custom domain → web.app.
 */
export function listHostingOriginCandidates(
  locationOrigin: string | null | undefined = typeof window !== 'undefined'
    ? window.location.origin
    : null,
): string[] {
  const out: string[] = [];
  pushUniqueHostingOrigin(out, import.meta.env.VITE_GAPI_PROXY_ORIGIN);
  pushUniqueHostingOrigin(out, import.meta.env.VITE_FUNCTIONS_ORIGIN);
  pushUniqueHostingOrigin(out, import.meta.env.VITE_WEBAUTHN_ORIGIN);
  pushUniqueHostingOrigin(out, locationOrigin);
  for (const fallback of FALLBACK_HOSTING_ORIGINS) {
    pushUniqueHostingOrigin(out, fallback);
  }
  return out.length > 0 ? out : [DEFAULT_PROXY_ORIGIN];
}

/**
 * Absolute origin that fronts `/gapi/**` (Hosting → Cloud Run).
 * Sync preference only — runtime health failover lives in `gapiConnectivity`.
 */
export function resolveGapiProxyOrigin(
  locationOrigin: string | null | undefined = typeof window !== 'undefined'
    ? window.location.origin
    : null,
): string {
  return listHostingOriginCandidates(locationOrigin)[0] ?? DEFAULT_PROXY_ORIGIN;
}

/** Host+path segment used by Firebase SDK `host` / `apiHost` fields (no scheme). */
export function gapiProxyHostPath(proxyOrigin: string, upstreamHost: string): string {
  const origin = stripTrailingSlash(proxyOrigin);
  let hostnameAndMaybePath: string;
  try {
    const url = new URL(origin);
    hostnameAndMaybePath = url.host + (url.pathname === '/' ? '' : url.pathname.replace(/\/$/, ''));
  } catch {
    hostnameAndMaybePath = origin.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  }
  return `${hostnameAndMaybePath}/gapi/${upstreamHost}`;
}

export function gapiProxyAbsoluteUrl(proxyOrigin: string, upstreamHost: string, path = ''): string {
  const base = `${stripTrailingSlash(proxyOrigin)}/gapi/${upstreamHost}`;
  if (!path) return base;
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

export type GapiProxyHosts = {
  origin: string;
  identityToolkit: string;
  secureToken: string;
  firestore: string;
  firebaseStorage: string;
  appCheckOrigin: string;
};

export function buildGapiProxyHosts(proxyOrigin: string): GapiProxyHosts {
  const origin = stripTrailingSlash(proxyOrigin);
  return {
    origin,
    identityToolkit: gapiProxyHostPath(origin, GAPI_UPSTREAM.identityToolkit),
    secureToken: gapiProxyHostPath(origin, GAPI_UPSTREAM.secureToken),
    firestore: gapiProxyHostPath(origin, GAPI_UPSTREAM.firestore),
    firebaseStorage: gapiProxyHostPath(origin, GAPI_UPSTREAM.firebaseStorage),
    appCheckOrigin: gapiProxyAbsoluteUrl(origin, GAPI_UPSTREAM.appCheck),
  };
}
